import React from 'react';
import { Check, ExternalLink, Loader2, Pin, Star, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const ReviewStars = ({ rating }) => (
  <div className="flex items-center gap-1 text-amber-500">
    {Array.from({ length: 5 }).map((_, index) => (
      <Star key={index} className={`h-4 w-4 ${index < rating ? 'fill-current' : 'text-gray-300'}`} />
    ))}
  </div>
);

export const AdminReviewsPanel = ({
  reviews,
  loading,
  busyReviewId,
  onApprove,
  onReject,
  onToggleFeatured,
}) => {
  const pendingReviews = reviews.filter((item) => item.status === 'pending');
  const approvedReviews = reviews.filter((item) => item.status === 'approved');

  return (
    <section className="space-y-4 mb-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Resenas</h2>
          <p className="text-sm text-gray-600">Aprueba o rechaza las nuevas resenas antes de mostrarlas en la landing.</p>
        </div>
        <div className="text-sm text-gray-500">
          {pendingReviews.length} pendientes · {approvedReviews.length} aprobadas
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" />
            Cargando resenas...
          </CardContent>
        </Card>
      ) : null}

      {!loading && pendingReviews.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {pendingReviews.map((review) => {
            const busy = busyReviewId === review.id;
            return (
              <Card key={review.id} className="border-amber-200 bg-amber-50/40">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg">{review.reviewer_name}</CardTitle>
                      <CardDescription>{review.property_name}{review.location ? ` · ${review.location}` : ''}</CardDescription>
                    </div>
                    <span className="inline-flex items-center rounded-md bg-amber-500 px-2 py-1 text-xs font-medium text-white">Pendiente</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ReviewStars rating={review.rating} />
                  <p className="text-sm leading-6 text-gray-700">{review.comment}</p>
                  {review.property_url ? (
                    <a
                      href={review.property_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Ver alojamiento
                    </a>
                  ) : null}
                  <div className="text-xs text-gray-500">{review.contact_email || 'Sin email'} · {new Date(review.created_at).toLocaleDateString('es-ES')}</div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => onApprove(review.id)} disabled={busy}>
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      Aprobar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReject(review.id)} disabled={busy}>
                      <X className="h-4 w-4" />
                      Rechazar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}

      {!loading && pendingReviews.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-gray-500">No hay resenas pendientes ahora mismo.</CardContent>
        </Card>
      ) : null}

      {!loading && approvedReviews.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resenas aprobadas</CardTitle>
            <CardDescription>Puedes destacar las que quieras mostrar primero en la landing.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {approvedReviews.slice(0, 8).map((review) => {
              const busy = busyReviewId === review.id;
              return (
                <div key={review.id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{review.reviewer_name}</p>
                        {review.featured ? <span className="inline-flex items-center rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white">Destacada</span> : null}
                      </div>
                      <p className="text-sm text-gray-500">{review.property_name}{review.location ? ` · ${review.location}` : ''}</p>
                      <ReviewStars rating={review.rating} />
                      <p className="text-sm leading-6 text-gray-700">{review.comment}</p>
                      {review.property_url ? (
                        <a
                          href={review.property_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Ver alojamiento
                        </a>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={review.featured ? 'secondary' : 'outline'}
                        onClick={() => onToggleFeatured(review.id, !review.featured)}
                        disabled={busy}
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pin className="h-4 w-4" />}
                        {review.featured ? 'Quitar destaque' : 'Destacar'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onReject(review.id)} disabled={busy}>
                        <X className="h-4 w-4" />
                        Retirar
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
};